import React from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMoon, faSun, faGlobe } from "@fortawesome/free-solid-svg-icons";
import { useTheme } from "../../contexts/ThemeContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { Theme } from "../../theme";

// アニメーション
const slideDown = keyframes`
	from {
		opacity: 0;
		transform: translateY(-10px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
`;

// Styled Components
const NavContainer = styled.div<{ theme: Theme }>`
	position: sticky;
	top: 0;
	z-index: 1000;
	padding: ${props => props.theme.density.navigationInset} 0;
	animation: ${slideDown} 0.3s ease-out;
`;

const Nav = styled.nav<{ theme: Theme }>`
	max-width: ${props => props.theme.density.contentMaxWidth};
	margin: 0 auto;
	padding: 0 ${props => props.theme.density.pageGutter};

	@media (max-width: 768px) {
		padding: 0 ${props => props.theme.density.pageGutterMobile};
	}
`;

const NavBackground = styled.div<{ theme: Theme }>`
	background: color-mix(in srgb, ${props => props.theme.colors.background.secondary} 86%, transparent);
	backdrop-filter: saturate(180%) blur(18px);
	-webkit-backdrop-filter: saturate(180%) blur(18px);
	border: 1px solid ${props => props.theme.colors.neutral[200]};
	border-radius: ${props => props.theme.density.cardRadius};
	padding: 0.25rem 0.375rem;
	box-shadow: ${props => props.theme.shadows.base};
	transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

	&:hover {
		box-shadow: ${props => props.theme.shadows.lg};
	}

`;

const NavContent = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 1rem;

	@media (max-width: 768px) {
		gap: 0.5rem;
	}
`;

const NavList = styled.ul`
	display: flex;
	flex: 1;
	justify-content: center;
	align-items: center;
	gap: 0.5rem;
	list-style: none;
	margin: 0;
	padding: 0;
	min-width: 0;
	overflow-x: auto;
	scrollbar-width: none;

	&::-webkit-scrollbar {
		display: none;
	}

	@media (max-width: 640px) {
		justify-content: flex-start;
		gap: 0;
	}
`;

const ControlsContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;

	@media (max-width: 640px) {
		gap: 0.25rem;
	}
`;

const ToggleButton = styled.button<{ theme: Theme }>`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.35rem;
	padding: 0.5rem 0.75rem;
	min-width: 44px;
	min-height: 44px;
	background: transparent;
	border: none;
	border-radius: 0.75rem;
	cursor: pointer;
	transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
	font-size: 1.25rem;
	color: ${props => props.theme.colors.text.primary};

	&:hover {
		background: ${props => props.theme.colors.background.secondary};
		transform: scale(1.1);
	}

	&:active {
		transform: scale(0.95);
	}

	@media (max-width: 640px) {
		padding: 0.4rem 0.6rem;
		font-size: 1rem;
		gap: 0.25rem;
	}
`;

const LanguageText = styled.span<{ theme: Theme }>`
	font-size: ${props => props.theme.typography.roles.caption.fontSize};
	font-weight: ${props => props.theme.typography.roles.label.fontWeight};
	letter-spacing: 0.05em;

	@media (max-width: 640px) {
		font-size: 0.7rem;
	}
`;

const NavItem = styled.li<{ isActive: boolean; theme: Theme }>`
	position: relative;
	flex: 0 0 auto;

	a {
		display: flex;
		align-items: center;
		min-height: 44px;
		padding: 0.5rem 0.75rem;
		color: ${props => props.isActive ? props.theme.colors.primary.main : props.theme.colors.text.primary};
		font-weight: ${props => props.isActive ? '600' : props.theme.typography.roles.label.fontWeight};
		font-size: ${props => props.theme.typography.roles.label.fontSize};
		border-radius: 0.75rem;
		text-decoration: none;
		transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
		background: ${props => props.isActive
			? props.theme.mode === 'light' ? '#f0f4ff' : props.theme.colors.neutral[100]
			: 'transparent'
		};
		position: relative;
		overflow: hidden;
		white-space: nowrap;

		&::before {
			content: '';
			position: absolute;
			top: 50%;
			left: 50%;
			width: 0;
			height: 0;
			border-radius: 50%;
			background: ${props => props.theme.mode === 'light' ? '#f0f4ff' : props.theme.colors.neutral[100]};
			transform: translate(-50%, -50%);
			transition: width 0.2s ease, height 0.2s ease;
			z-index: -1;
		}

		&:hover {
			color: ${props => props.theme.colors.primary.main};
			transform: translateY(-2px);

			&::before {
				width: 150%;
				height: 150%;
			}
		}

		&:active {
			transform: translateY(0);
		}

		@media (max-width: 768px) {
			padding: 0.5rem 0.625rem;
			font-size: 0.9rem;
		}

		@media (max-width: 640px) {
			padding: 0.5rem 0.5rem;
			font-size: 0.85rem;
		}
	}
`;

interface NavBarProps {
	active?: string;
}

const NavBar: React.FC<NavBarProps> = (props) => {
	const { active } = props;
	const location = useLocation();
	const { theme, themeMode, toggleTheme } = useTheme();
	const { language, t, toggleLanguage } = useLanguage();

	// 現在のパスからアクティブなページを判定
	const isActive = (page: string): boolean => {
		if (active) {
			return active === page;
		}
		const path = location.pathname;
		if (page === "home") return path === "/";
		return path.startsWith(`/${page}`);
	};

	return (
		<NavContainer theme={theme}>
			<Nav theme={theme}>
				<NavBackground theme={theme}>
					<NavContent>
						<NavList>
							<NavItem theme={theme} isActive={isActive("home")}>
								<Link to="/">{t.nav.home}</Link>
							</NavItem>
							<NavItem theme={theme} isActive={isActive("about")}>
								<Link to="/about">{t.nav.about}</Link>
							</NavItem>
							<NavItem theme={theme} isActive={isActive("projects")}>
								<Link to="/projects">{t.nav.projects}</Link>
							</NavItem>
							<NavItem theme={theme} isActive={isActive("articles")}>
								<Link to="/articles">{t.nav.outputs}</Link>
							</NavItem>
							<NavItem theme={theme} isActive={isActive("services")}>
								<Link to="/services">{t.nav.services}</Link>
							</NavItem>
							<NavItem theme={theme} isActive={isActive("contact")}>
								<Link to="/contact">{t.nav.contact}</Link>
							</NavItem>
						</NavList>
						<ControlsContainer>
							<ToggleButton
								theme={theme}
								onClick={toggleTheme}
								aria-label="Toggle theme"
								title={themeMode === 'light' ? 'ダークモードに切り替え' : 'ライトモードに切り替え'}
							>
								<FontAwesomeIcon icon={themeMode === 'light' ? faMoon : faSun} />
							</ToggleButton>
							<ToggleButton
								theme={theme}
								onClick={toggleLanguage}
								aria-label="Toggle language"
								title={language === 'ja' ? 'Switch to English' : '日本語に切り替え'}
							>
								<FontAwesomeIcon icon={faGlobe} />
								<LanguageText theme={theme}>
									{language === 'ja' ? 'JA' : 'EN'}
								</LanguageText>
							</ToggleButton>
						</ControlsContainer>
					</NavContent>
				</NavBackground>
			</Nav>
		</NavContainer>
	);
};

export default NavBar;
